import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServices } from "@/services-context";
import { LeadCreateDocument } from "@/generated/graphql/b2c";
import style from "@/pages-react/gone-page/gone-page.module.scss";
import { Input } from "@/uikit";
import { Icon } from "@/elements-react/fix-icon";
import hashIcon from "@/assets/gone-page/hashIcon.svg";
import classNames from "classnames";
import mailIcon from "@/assets/gone-page/mailIcon.svg";
import { FixButton } from "@/elements-react/fix-button";
import arrowRight from "@/assets/arrow-outlined-right.svg";

export function useGonePageCommon(mpn?: string) {
  const { t } = useTranslation();
  const { api } = useServices();

  const formRef = useRef<HTMLFormElement>(null);

  // ToDo: получать mpn из startup request после готовности SSR

  const [loading, setLoading] = useState(false);
  const [buttonText, setButtonText] = useState(t("4760av68odya8f02.send-request", "Send request"));
  const [formIsSuccess, setFormIsSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({
    email: "",
    vin: "",
  });
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [formData, setFormData] = useState({ email: "", vin: "" });

  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target as HTMLInputElement;

      setFormData((prev) => ({ ...prev, [name]: value }));

      if (formErrors[name]) {
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [formErrors],
  );

  const submit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      const { email, vin } = formData;
      const newErrors: { [key: string]: string } = {};
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      // Валидация пустых полей
      if (!email.trim() || !emailRegex.test(email)) {
        newErrors.email = t("x926694e5jfby7au.email-incorrect", "Please enter a valid email address");
      }

      if (!vin.trim()) {
        newErrors.vin = t("E66B8B634DC2C86D.enter-vin", "Please enter VIN");
      }

      if (Object.keys(newErrors).length > 0) {
        setFormErrors(newErrors);
        return;
      }

      if (!formRef.current?.checkValidity()) {
        return;
      }

      setLoading(true);

      try {
        const result = await api.client.mutate({
          mutation: LeadCreateDocument,
          variables: {
            input: {
              mail: email,
              formName: "gone_page_form",
              attributes: JSON.stringify({ vin: vin, mpn: mpn }),
              name: "",
              source: "",
              phone: "",
            },
          },
        });

        setLoading(false);

        if (!result?.data?.LeadCreate) {
          // @ts-ignore
          setFormErrors({ ...result.errors });
          return;
        }

        setButtonDisabled(true);
        setButtonText(t("47ac4343vdya8f02.submitted", "Submitted!"));
        setFormIsSuccess(true);
      } catch (error) {
        setLoading(false);
        // Обработка ошибок
      }
    },
    [formData, mpn, api, t],
  );

  const renderForm = () => {
    return (
      <form ref={formRef} className={style.form}>
        <div className={style.formTitles}>
          <div className={style.formTitle}>
            {t("5E17295914160C58.need-help-finding-part", "Need help finding part?")}
          </div>
          <div className={style.formSubtitle}>
            {t("5A8021852995F2B4.discuss-suitable-options", "We'll get in touch to discuss suitable options with you")}
          </div>
        </div>
        <div className={style.inputsContainer}>
          <Input
            name="vin"
            type="text"
            placeholder="VIN"
            customError={formErrors.vin}
            clearable
            required
            value={formData.vin}
            onChange={changeHandler}
            icon={<Icon src={hashIcon} width={17} height={17} className={classNames(style.prefixIcon)} />}
            iconPosition="prefix"
          />
          <Input
            id="email-input"
            name="email"
            type="email"
            placeholder={t("9A9C1A1F52B34DD9.your-email", "Your email")}
            customError={formErrors.email}
            clearable
            required
            value={formData.email}
            onChange={changeHandler}
            icon={<Icon src={mailIcon} width={17} height={17} className={classNames(style.prefixIcon)} />}
            iconPosition="prefix"
          />
        </div>
        <FixButton onClick={submit} variant="primary" size="large" loading={loading} disabled={buttonDisabled}>
          {!formIsSuccess && <Icon src={arrowRight} width={16} height={16} />}
          {buttonText}
        </FixButton>
      </form>
    );
  };

  return {
    mpn,
    formRef,
    formErrors,
    changeHandler,
    formData,
    submit,
    loading,
    buttonDisabled,
    formIsSuccess,
    buttonText,
    renderForm,
  };
}
